import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { CheckCircle, SpinnerGap, CreditCard, ArrowLeft, XCircle, Crown } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAYPAL_CLIENT_ID = "Ac9GAxYrJ4xCmjRE3kPFb4597cgXlIq0G3QusJ5RqzehZOdwHUu4Om7rE-t-iFyNe7OXihwS-Lj6C92-";

export default function SubscriptionPage({ onBack }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);

  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    if (attempts >= 5) { setPaymentResult('error'); return; }
    setCheckingStatus(true);
    try {
      const { data } = await axios.get(`${API}/subscription/status/${sessionId}`, { withCredentials: true });
      if (data.payment_status === 'paid') { setPaymentResult('success'); setCheckingStatus(false); }
      else if (data.status === 'expired') { setPaymentResult('error'); setCheckingStatus(false); }
      else { setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000); }
    } catch (error) { console.error("Poll error:", error.message); setPaymentResult('error'); setCheckingStatus(false); }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const path = window.location.pathname;
    if (path.includes('/subscription/success') && sessionId) pollPaymentStatus(sessionId);
    else if (path.includes('/subscription/cancel')) setPaymentResult('cancel');
  }, [pollPaymentStatus]);

  const handleStripeCheckout = async () => {
    setLoading(true);
    try {
      const origin = window.location.origin;
      const { data } = await axios.post(`${API}/subscription/checkout`, { plan: 'pro', origin_url: origin }, { withCredentials: true });
      if (data.url) window.location.href = data.url;
    } catch (err) { alert(err.response?.data?.detail || 'Erreur'); }
    finally { setLoading(false); }
  };

  const handlePayPalApprove = async (data) => {
    try {
      const resp = await axios.post(`${API}/paypal/capture-order/${data.orderID}`, {}, { withCredentials: true });
      if (resp.data.status === 'completed') {
        setPaymentResult('success');
      }
    } catch (error) {
      console.error("PayPal capture error:", error.message);
      setPaymentResult('error');
    }
  };

  if (paymentResult === 'success') return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center p-6" data-testid="payment-success">
      <div className="bg-[#18181B] border border-[#27272A] rounded-lg p-8 max-w-md w-full text-center animate-fade-in-up">
        <div className="w-16 h-16 bg-green-500/15 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle size={36} weight="duotone" className="text-green-400" />
        </div>
        <h2 className="text-2xl font-bold tracking-tight mb-2 text-[#F4F4F5]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Bienvenue en Pro !</h2>
        <p className="text-[#A1A1AA] mb-6">Accès illimité à tous les outils de génération.</p>
        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#8B5CF6]/15 rounded-full mb-6">
          <Crown size={14} weight="fill" className="text-[#A78BFA]" /><span className="text-xs font-bold text-[#A78BFA]">Plan Pro activé</span>
        </div><br />
        <button onClick={onBack} data-testid="back-to-dashboard" className="h-12 px-6 bg-[#8B5CF6] text-white font-bold rounded-lg hover:bg-[#7C3AED] transition-colors inline-flex items-center gap-2">Retour au générateur</button>
      </div>
    </div>
  );

  if (paymentResult === 'cancel') return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center p-6" data-testid="payment-cancel">
      <div className="bg-[#18181B] border border-[#27272A] rounded-lg p-8 max-w-md w-full text-center animate-fade-in-up">
        <XCircle size={48} weight="duotone" className="text-[#71717A] mx-auto mb-4" />
        <h2 className="text-2xl font-bold tracking-tight mb-2 text-[#F4F4F5]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Paiement annulé</h2>
        <p className="text-[#A1A1AA] mb-6">Pas de souci ! Vous pouvez réessayer quand vous voulez.</p>
        <button onClick={onBack} className="h-12 px-6 bg-[#27272A] text-[#F4F4F5] font-bold rounded-lg hover:bg-[#3F3F46] transition-colors inline-flex items-center gap-2"><ArrowLeft size={18} /> Retour</button>
      </div>
    </div>
  );

  if (checkingStatus) return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center p-6" data-testid="payment-checking">
      <div className="text-center animate-fade-in">
        <SpinnerGap size={40} className="animate-spin text-[#8B5CF6] mx-auto mb-4" />
        <p className="text-[#A1A1AA] font-medium">Vérification du paiement...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="subscription-page">
      <div className="border-b border-[#27272A]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-14 flex items-center">
          <button onClick={onBack} className="flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-[#F4F4F5] transition-colors" data-testid="back-btn"><ArrowLeft size={18} /> Retour</button>
        </div>
      </div>
      <div className="max-w-lg mx-auto px-4 sm:px-6 py-12">
        <div className="text-center mb-8 animate-fade-in-up">
          <Crown size={36} weight="duotone" className="text-[#A78BFA] mx-auto mb-3" />
          <h1 className="text-3xl font-black tracking-tight mb-2 text-[#F4F4F5]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Passer en Pro</h1>
          <p className="text-[#A1A1AA]">Posts illimités, mode Avant/Après, galerie publique</p>
        </div>
        <div className="bg-[#18181B] border border-[#8B5CF6] ring-1 ring-[#8B5CF6] rounded-lg p-6 mb-6 animate-fade-in-up stagger-1">
          <div className="flex items-baseline gap-2 mb-4">
            <span className="text-4xl font-black tracking-tight text-[#F4F4F5]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>17,90€</span>
            <span className="text-[#71717A]">/mois</span>
          </div>
          <ul className="space-y-2 mb-6">
            {["Posts illimités","Mode Avant/Après","Multi-images (5 max)","Galerie publique","Tous les templates","Support prioritaire"].map(f => (
              <li key={f} className="flex items-center gap-2 text-sm text-[#E4E4E7]"><CheckCircle size={16} weight="duotone" className="text-[#8B5CF6] shrink-0" /> {f}</li>
            ))}
          </ul>

          {/* Stripe */}
          <button onClick={handleStripeCheckout} disabled={loading} data-testid="stripe-checkout-btn"
            className="w-full h-14 bg-[#8B5CF6] text-white font-bold text-base rounded-lg hover:bg-[#7C3AED] transition-colors flex items-center justify-center gap-2 mb-3 disabled:opacity-60">
            {loading ? <><SpinnerGap size={20} className="animate-spin" /> Redirection...</> : <><CreditCard size={22} weight="duotone" /> Payer par carte bancaire</>}
          </button>

          {/* PayPal */}
          <div className="mb-3" data-testid="paypal-button-container">
            <PayPalScriptProvider options={{ clientId: PAYPAL_CLIENT_ID, currency: "EUR" }}>
              <PayPalButtons
                style={{ layout: "horizontal", color: "blue", shape: "rect", label: "pay", height: 55 }}
                createOrder={async () => {
                  const { data } = await axios.post(`${API}/paypal/create-order`, {}, { withCredentials: true });
                  return data.order_id;
                }}
                onApprove={handlePayPalApprove}
                onError={(err) => { console.error("PayPal error:", err); setPaymentResult('error'); }}
              />
            </PayPalScriptProvider>
          </div>

          <p className="text-xs text-center text-[#71717A] mt-3">Paiement sécurisé. Annulable à tout moment.</p>
        </div>
      </div>
    </div>
  );
}
